import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { COLOR_SCHEME, CLASSIC } from '../styling/ColorSchemes';
import { FONT_THEME } from '../styling/FontSchemes';

interface StyleContextType {
  colorScheme: COLOR_SCHEME;
  setColorScheme: (scheme: COLOR_SCHEME) => void;
  fontScheme: typeof FONT_THEME[keyof typeof FONT_THEME]; // actual object
  setFontScheme: (schemeKey: keyof typeof FONT_THEME) => void;
}

const StyleContext = createContext<StyleContextType | undefined>(undefined);

export const useStyle = () => {
  const context = useContext(StyleContext);
  if (!context) {
    throw new Error('useStyle must be used within a StyleProvider');
  }
  return context;
};

// Google Fonts URLs keyed by scheme. Inter is already loaded globally; Classic needs no extra load.
const FONT_SCHEME_URLS: Partial<Record<keyof typeof FONT_THEME, string>> = {
  // ── Core ──────────────────────────────────────────────────────────────────
  MODERN:    'https://fonts.googleapis.com/css2?family=Bodoni+Moda+SC:ital,opsz,wght@0,6..96,400;0,6..96,700&display=swap',
  ELEGANT:   'https://fonts.googleapis.com/css2?family=Great+Vibes&family=Cormorant+Garamond:wght@400;500;700&family=Tangerine:wght@400;700&display=swap',
  FRIENDLY:  'https://fonts.googleapis.com/css2?family=Bagel+Fat+One&family=Nunito:wght@400;600&family=Comfortaa:wght@400;600&display=swap',

  // ── Script / Calligraphy ──────────────────────────────────────────────────
  SIGNATURE: 'https://fonts.googleapis.com/css2?family=Mr+Dafoe&family=Lato:wght@400;600&family=Raleway:wght@400;600&display=swap',
  FESTIVE:   'https://fonts.googleapis.com/css2?family=Festive&family=Cormorant+Garamond:wght@400;700&family=Cinzel+Decorative:wght@400;700&display=swap',
  SPLASH:    'https://fonts.googleapis.com/css2?family=Splash&family=Nunito:wght@400;600&family=Comfortaa:wght@400;600&display=swap',

  // ── Editorial / Serif ─────────────────────────────────────────────────────
  EDITORIAL: 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Lora:wght@400;600&family=Montserrat:wght@400;600&display=swap',

  // ── Vintage / Themed ──────────────────────────────────────────────────────
  WESTERN:   'https://fonts.googleapis.com/css2?family=Rye&family=Libre+Baskerville:wght@400;700&family=Josefin+Slab:wght@400;600&display=swap',
  RETRO:     'https://fonts.googleapis.com/css2?family=Fascinate&family=Raleway:wght@400;600&family=Oswald:wght@400;600&display=swap',
  GOTHIC:    'https://fonts.googleapis.com/css2?family=UnifrakturMaguntia&family=EB+Garamond:wght@400;600&family=Cinzel:wght@400;600&display=swap',

  // ── Artsy / Handmade ──────────────────────────────────────────────────────
  SKETCH:    'https://fonts.googleapis.com/css2?family=Cabin+Sketch:wght@400;700&family=Cabin:wght@400;600&family=Josefin+Sans:wght@400;600&display=swap',
  MOSAIC:    'https://fonts.googleapis.com/css2?family=Palette+Mosaic&family=DM+Sans:opsz,wght@9..40,400;9..40,600&family=Nunito:wght@400;600&display=swap',

  // ── Urban / Bold ──────────────────────────────────────────────────────────
  SPRAY:     'https://fonts.googleapis.com/css2?family=Rubik+Spray+Paint&family=Rubik:wght@400;600&family=Space+Grotesk:wght@400;600&display=swap',
  GLITCH:    'https://fonts.googleapis.com/css2?family=Rubik+Glitch&family=Rubik:wght@400;600&family=Space+Grotesk:wght@400;600&display=swap',

  // ── Pixel / Digital ───────────────────────────────────────────────────────
  PIXEL:     'https://fonts.googleapis.com/css2?family=Jacquard+12&family=VT323&family=Space+Mono:wght@400;700&display=swap',
  JERSEY:    'https://fonts.googleapis.com/css2?family=Jersey+15&family=Bebas+Neue&family=Oswald:wght@400;600&display=swap',
  ARCADE:    'https://fonts.googleapis.com/css2?family=Jacquard+24&family=Share+Tech+Mono&family=VT323&display=swap',
};

const loadedFontUrls = new Set<string>();

function injectFontStylesheet(url: string): void {
  if (loadedFontUrls.has(url)) return;
  loadedFontUrls.add(url);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  document.head.appendChild(link);
}

/** Loads the stylesheet required by one saved font theme on public pages. */
export function preloadFontScheme(fontScheme: keyof typeof FONT_THEME): void {
  const url = FONT_SCHEME_URLS[fontScheme];
  if (url) injectFontStylesheet(url);
}

/** Eagerly injects all font stylesheets — call this when the font picker panel opens. */
export function preloadAllFontSchemes(): void {
  Object.values(FONT_SCHEME_URLS).forEach((url) => {
    if (url) injectFontStylesheet(url);
  });
}

interface StyleProviderProps {
  children: ReactNode;
  defaultColorScheme?: COLOR_SCHEME;
  defaultFontScheme?: keyof typeof FONT_THEME;
}

export const StyleProvider: React.FC<StyleProviderProps> = ({
  children,
  defaultColorScheme = CLASSIC,
  defaultFontScheme = 'CLASSIC',
}) => {
  const [colorScheme, setColorScheme] = useState<COLOR_SCHEME>(defaultColorScheme);
  const [fontScheme, setFontScheme] = useState<keyof typeof FONT_THEME>(defaultFontScheme);

  // Lazily inject the font CSS for non-Classic schemes the first time they're used.
  useEffect(() => {
    preloadFontScheme(fontScheme);
  }, [fontScheme]);

  const value: StyleContextType = {
    colorScheme,
    setColorScheme,
    fontScheme: FONT_THEME[fontScheme],
    setFontScheme,
  };

  return <StyleContext.Provider value={value}>{children}</StyleContext.Provider>;
};
